import { useEffect, useState } from "react";
import { guestOpacApi } from "@/lib/api/guest-opac";
import { useTenant } from "@/contexts/TenantContext";

interface GuestOpacBranding {
  libraryName: string;
  logoUrl: string | null;
  isLoading: boolean;
}

export function useGuestOpacBranding(tenantSlug?: string): GuestOpacBranding {
  const { tenant } = useTenant();
  const [logoUrl, setLogoUrl] = useState<string | null>(tenant?.logoUrl ?? null);
  const [libraryName, setLibraryName] = useState(tenant?.name ?? "Library");
  const [isLoading, setIsLoading] = useState(Boolean(tenantSlug));

  useEffect(() => {
    if (!tenantSlug) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    guestOpacApi
      .getConfig(tenantSlug)
      .then((config) => {
        if (cancelled) return;
        setLibraryName(config.tenant.name);
        setLogoUrl(config.branding.logoUrl ?? tenant?.logoUrl ?? null);
        const primaryColor = config.branding.primaryColor;
        if (primaryColor) {
          applyPrimaryColor(primaryColor);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLibraryName(tenant?.name ?? "Library");
          setLogoUrl(tenant?.logoUrl ?? null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantSlug, tenant?.name, tenant?.logoUrl]);

  return { libraryName, logoUrl, isLoading };
}

function applyPrimaryColor(primaryColor: string) {
  const hex = primaryColor.replace("#", "");
  if (hex.length !== 6) return;
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  const hsl = `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  document.documentElement.style.setProperty("--primary", hsl);
  document.documentElement.style.setProperty("--ring", hsl);
}
